import React, { useState } from 'react';
import { 
    Alert, 
    AlertTitle, 
    Box, 
    Button, 
    CardContent, 
    CircularProgress, 
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, ExpandMore, Refresh, ContentCopy } from '@mui/icons-material';
import { useGetContractualDataTestQuery } from '../../redux/cutterHistoryAPI';
import { CopyToClipboard } from '../../common/CopyToClipBoard';
import { getFormatedStringFromDate } from '../../utils/dateUtils';
import HelpIcon from '../../help/HelpIcon';

interface CutterDetailsContractualDataTestProps {
    serialNumber: string;
}

export const CutterDetailsContractualDataTest: React.FC<CutterDetailsContractualDataTestProps> = ({ serialNumber }) => {
    const [shouldFetch, setShouldFetch] = useState(false);
    
    const { data, error, isLoading, isFetching, refetch } = useGetContractualDataTestQuery(serialNumber, {
        skip: !shouldFetch
    });

    const handleTest = (): void => {
        setShouldFetch(true);
        if (shouldFetch) {
            void refetch();
        }
    };

    // Parse the serialized JSON data
    const parsedData = data?.data ? JSON.parse(data.data) : null;
    const isSuccess = data?.success ?? false;
    const technicalDetails = data
        ? { ...data, data: parsedData ?? data.data }
        : error;

    // Common props for labels (matching factory tab style)
    const labelProps = {
        variant: "subtitle2" as const,
        component: "span" as const,
        sx: { color: 'text.secondary', fontWeight: 300 }
    };

    // Common props for values (matching factory tab style)
    const valueProps = {
        variant: "subtitle2" as const,
        component: "span" as const,
        sx: { color: 'text.primary', fontWeight: 500 }
    };

    return (
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingX:'1em', alignItems: 'center', marginBottom: 2 }}>
                <Typography variant="body2" sx={{ maxWidth: '60%' }}>
                    This interface lets you test the ERP data contract as displayed on the right side of the Pilot login page.
                </Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={isLoading || isFetching ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                    onClick={handleTest}
                    disabled={isLoading || isFetching}
                >
                    {isLoading || isFetching ? 'Testing...' : 'Get Contract Consumption'}
                </Button>
            </Box>

            {/* Instructions */}
            {!data && !isLoading && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: (theme) => theme.palette.info.light,
                    color: (theme) => theme.palette.info.contrastText,
                    borderRadius: 1
                }}>
                    <Typography variant="body2">
                        Click &quot;Get Contract Consumption&quot; from <strong>cutter-report.lectra.com</strong> to test: <strong>{serialNumber}</strong>
                    </Typography>
                </Box>
            )}

            {/* Single Result Box */}
            {data && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                        <Alert 
                            severity={isSuccess ? 'success' : 'error'}
                            icon={isSuccess ? <CheckCircle /> : <ErrorIcon />}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            <AlertTitle>
                            {isSuccess 
                                ? `Successfully retrieved contractual data for ${serialNumber}`
                                : `Failed to retrieve contractual data for ${serialNumber}`
                                }
                            </AlertTitle>
                        </Alert>
                        <Box sx={{ flexShrink: 0, pt: 1, display: 'flex', gap: 1 }}>
                            <CopyToClipboard
                                TextToCopy={JSON.stringify(parsedData, null, 2)}
                                title='Copy response to clipboard'
                                AckMessage='Response copied to clipboard'>
                                <ContentCopy 
                                    sx={{ 
                                        color: isSuccess ? 'success.main' : 'error.main', 
                                        cursor: 'pointer' 
                                    }} 
                                    titleAccess="Copy response" 
                                />
                            </CopyToClipboard>
                        </Box>
                    </Box>

                    {/* Display Success Response as Pretty Form */}
                    {isSuccess && parsedData ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" component="div" sx={{ color: 'secondary.main', mb: 2 }} >
                                Contract Information : {parsedData.SerialNumber}
                            </Typography>
                            
                            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.5, px: 0 } }}>
                                <TableBody>

                                    
                                    <TableRow>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography {...labelProps}>Contract Period:</Typography>
                                                <HelpIcon helpContext="erp-contract-period" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps}>
                                                {getFormatedStringFromDate(new Date(parsedData.StartDate))} → {getFormatedStringFromDate(new Date(parsedData.EndDate))}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography {...labelProps}>Contractual Cutting Time:</Typography>
                                                <HelpIcon helpContext="erp-contract-contractual-time" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps}>
                                                {parseFloat(parsedData.ContractualCuttingTime) === 0
                                                    ? <Typography component="span" sx={{ color: 'info.main', fontStyle: 'italic' }}>Unlimited ({parsedData.ContractualCuttingTimeCategory})</Typography>
                                                    : <>{parsedData.ContractualCuttingTime} hours ({parsedData.ContractualCuttingTimeCategory})</>}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography {...labelProps}>Additional Granted Time:</Typography>
                                                <HelpIcon helpContext="erp-contract-additional-time" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps}>{parsedData.AdditionalGrantedTime} hours</Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography {...labelProps}>Reference Cutting Time:</Typography>
                                                <HelpIcon helpContext="erp-contract-reference-time" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps}>
                                                {parseFloat(parsedData.ReferenceCuttingTime).toFixed(2)} hours
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    {parsedData.LastOracleEffectiveCuttingTime && (
                                        <>
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography {...labelProps}>Last Effective Cutting Time:</Typography>
                                                        <HelpIcon helpContext="erp-contract-last-effective" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography {...valueProps}>
                                                        {parseFloat(parsedData.LastOracleEffectiveCuttingTime.Value).toFixed(2)} hours
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography {...labelProps}>Last Update Date:</Typography>
                                                        <HelpIcon helpContext="erp-contract-last-update" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography {...valueProps}>
                                                        {getFormatedStringFromDate(new Date(parsedData.LastOracleEffectiveCuttingTime.Date))}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>

                                            {/* ── Separator: raw data above / computed values below ── */}
                                            <TableRow>
                                                <TableCell colSpan={2} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                                            </TableRow>

                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography {...labelProps}>Hours Worked Since Contract Start:</Typography>
                                                        <HelpIcon helpContext="erp-contract-worked-hours" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const workedHours = parseFloat(parsedData.LastOracleEffectiveCuttingTime.Value) - parseFloat(parsedData.ReferenceCuttingTime);
                                                        return (
                                                            <Typography {...valueProps}>
                                                                {workedHours.toFixed(2)} hours
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography {...labelProps}>Remaining Time:</Typography>
                                                        <HelpIcon helpContext="erp-contract-remaining-time" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const isUnlimited = parseFloat(parsedData.ContractualCuttingTime) === 0;
                                                        if (isUnlimited) {
                                                            const endDate = new Date(parsedData.EndDate);
                                                            const today = new Date();
                                                            const daysLeft = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                                            if (daysLeft <= 0) {
                                                                return (
                                                                    <Typography {...valueProps} sx={{ color: 'error.main' }}>
                                                                        Contract expired {Math.abs(daysLeft)} day{Math.abs(daysLeft) !== 1 ? 's' : ''} ago
                                                                    </Typography>
                                                                );
                                                            }
                                                            return (
                                                                <Typography {...valueProps} sx={{ color: daysLeft < 30 ? 'warning.main' : 'info.main' }}>
                                                                    {daysLeft} day{daysLeft !== 1 ? 's' : ''} until contract ends
                                                                    {' '}
                                                                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                                                                        (unlimited cutting time)
                                                                    </Typography>
                                                                </Typography>
                                                            );
                                                        }
                                                        const workedHours = parseFloat(parsedData.LastOracleEffectiveCuttingTime.Value) - parseFloat(parsedData.ReferenceCuttingTime);
                                                        const remainingHours = parseFloat(parsedData.ContractualCuttingTime) - workedHours;

                                                        if (remainingHours < 0) {
                                                            return (
                                                                <Typography {...valueProps} sx={{ color: 'error.main' }}>
                                                                    {Math.abs(remainingHours).toFixed(2)} hours over contractual limit
                                                                </Typography>
                                                            );
                                                        }

                                                        return (
                                                            <Typography {...valueProps} sx={{ color: remainingHours < parseFloat(parsedData.ContractualCuttingTime) * 0.1 ? 'warning.main' : 'success.main' }}>
                                                                {remainingHours.toFixed(2)} hours
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            </TableRow>
                                            
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography {...labelProps}>Usage Percentage:</Typography>
                                                        <HelpIcon helpContext="erp-contract-usage-pct" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const isUnlimited = parseFloat(parsedData.ContractualCuttingTime) === 0;
                                                        if (isUnlimited) {
                                                            return (
                                                                <Typography {...valueProps} sx={{ color: 'info.main', fontStyle: 'italic' }}>
                                                                    N/A – Unlimited contract
                                                                </Typography>
                                                            );
                                                        }
                                                        const workedHours = parseFloat(parsedData.LastOracleEffectiveCuttingTime.Value) - parseFloat(parsedData.ReferenceCuttingTime);
                                                        const pct = (workedHours / parseFloat(parsedData.ContractualCuttingTime)) * 100;
                                                        const color = pct >= 100 ? 'error.main' : pct >= 90 ? 'warning.main' : 'success.main';
                                                        return (
                                                            <Typography {...valueProps} sx={{ color }}>
                                                                {pct.toFixed(2)}%
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : null}

                    {/* Display Error Response */}
                    {!isSuccess && parsedData ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" component="div" sx={{ color: 'error.main', mb: 2 }}>
                                Error Information
                            </Typography>
                            
                            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.5, px: 0 } }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ width: '250px' }}>
                                            <Typography {...labelProps}>Serial Number:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps}>{serialNumber}</Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Typography {...labelProps}>Error Type:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps} sx={{ color: 'error.main' }}>
                                                {parsedData.ClassName?.includes('CommunicationObjectFaultedException') 
                                                    ? 'No Active Contract Found' 
                                                    : parsedData.ExceptionType ?? parsedData.ClassName ?? 'Service Error'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Typography {...labelProps}>Description:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps}>
                                                {parsedData.ClassName?.includes('CommunicationObjectFaultedException')
                                                    ? 'This cutter does not have a Maintenance contract with cutting time allocation in our ERP system.'
                                                    : parsedData.Message ?? 'An error occurred while retrieving contract data.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Typography {...labelProps}>Possible Causes:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyleType: 'disc' }}>
                                                <Box component="li" sx={{ paddingY: 0.25 }}>
                                                    <Typography variant="caption">No contract configured in ERP for this serial number</Typography>
                                                </Box>
                                                <Box component="li" sx={{ paddingY: 0.25 }}>
                                                    <Typography variant="caption">Contract has expired or not yet started</Typography>
                                                </Box>
                                                <Box component="li" sx={{ paddingY: 0.25 }}>
                                                    <Typography variant="caption">ERP service temporarily unavailable</Typography>
                                                </Box>
                                                {parsedData.Source && (
                                                    <Box component="li" sx={{ paddingY: 0.25 }}>
                                                        <Typography variant="caption">Service: {parsedData.Source}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow>
                                        <TableCell>
                                            <Typography {...labelProps}>Recommended Action:</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography {...valueProps} sx={{ color: 'warning.main' }}>
                                                Contact ERP administrator or check the Contract History tab for historical contract data
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                            
                        </Box>
                    ) : null}
                </Box>
            )}

            {shouldFetch && !isLoading && !isFetching && technicalDetails && (
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Technical Details
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{
                            backgroundColor: (theme) => theme.palette.error.light,
                            color: (theme) => theme.palette.error.contrastText,
                            p: 2,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            overflow: 'auto',
                            maxHeight: '300px'
                        }}>
                            {JSON.stringify(technicalDetails, null, 2)}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            
        </CardContent>
    );
};

export default CutterDetailsContractualDataTest;
